import { faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import Flex from 'components/common/Flex';
import { CUSTOM_REPORT_FIELDS } from 'constants/customReportFields';
import api from 'helpers/api';
import PropTypes from 'prop-types';
import { useAppContext } from 'providers/AppProvider';
import CustomReportProvider, {
  useCustomReportContext
} from 'providers/CustomReportProvider';
import { useEffect, useState } from 'react';
import { Button, Card } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import ContactsService from 'services/Contacts.service';
import CustomReportService from 'services/CustomReport.service';
import ServiceCatalogService from 'services/ServiceCatalog.service';
import ServiceGroupService from 'services/ServiceGroup.service';
import TypeTicketsService from 'services/TypeTickets.service';
import CustomReportLayout from './CustomReportLayout';
import './CustomReportWizard.css';
import BasicDefinitionsStep from './wizard-steps/BasicDefinitionsStep';
import DataAndFiltersStep from './wizard-steps/DataAndFiltersStep';
import SchedulingStep from './wizard-steps/SchedulingStep';
import SummaryStep from './wizard-steps/SummaryStep';

const CustomReportWizardContent = () => {
  const { isRTL } = useAppContext();
  const navigate = useNavigate();
  const { uuid } = useParams();
  const isEditMode = !!uuid;
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const http = api();

  const {
    currentStep,
    setCurrentStep,
    reportData,
    setReportData,
    resetReport
  } = useCustomReportContext();

  const navItems = [
    {
      icon: 'file-alt',
      label: '1. Definições Básicas'
    },
    {
      icon: 'search',
      label: '2. Dados e Filtros'
    },
    {
      icon: 'clock',
      label: '3. Agendamento'
    },
    {
      icon: 'check-circle',
      label: '4. Finalização'
    }
  ];

  useEffect(() => {
    if (isEditMode) {
      loadReport();
    }
    return () => {
      resetReport();
    };
  }, [uuid]);

  // Função auxiliar para resolver o nome do cliente pelo ID
  const resolveCustomerName = async customerId => {
    try {
      if (!customerId) return customerId;
      const customer = await http.customers.findById(customerId);
      return customer && !customer.ok ? customer.name : customerId;
    } catch (error) {
      console.error('Erro ao buscar cliente:', error);
      return customerId;
    }
  };

  // Função auxiliar para resolver o nome do contato pelo ID
  const resolveContactName = async contactId => {
    try {
      if (!contactId) return contactId;
      // Busca todos os contatos e filtra pelo ID
      const response = await ContactsService.list('size=1000');
      if (response && !response.ok && response.content) {
        const contact = response.content.find(c => c.id == contactId);
        return contact ? contact.name : contactId;
      }
      return contactId;
    } catch (error) {
      console.error('Erro ao buscar contato:', error);
      return contactId;
    }
  };

  // Função auxiliar para resolver o nome do agente pelo ID
  const resolveAgentName = async agentId => {
    try {
      if (!agentId) return agentId;

      const response = await api().agents.listSummaryData();
      if (response && Array.isArray(response)) {
        const agent = response.find(a => a.id == agentId);
        return agent ? `${agent.firstName} ${agent.lastName}`.trim() : agentId;
      }
      return agentId;
    } catch (error) {
      console.error('Erro ao buscar agente:', error);
      return agentId;
    }
  };

  // Função auxiliar para resolver o nome do grupo de serviço pelo ID
  const resolveServiceGroupName = async groupId => {
    try {
      if (!groupId) return groupId;

      const response = await ServiceGroupService.listSummaryData(
        'page=0&size=1000&includeInactives=false'
      );
      if (response && response.content && Array.isArray(response.content)) {
        const group = response.content.find(g => g.id == groupId);
        return group ? group.name : groupId;
      }
      return groupId;
    } catch (error) {
      console.error('Erro ao buscar grupo de serviço:', error);
      return groupId;
    }
  };

  // Função auxiliar para resolver o nome do tipo de ticket pelo ID
  const resolveTypeTicketName = async typeId => {
    try {
      if (!typeId) return typeId;

      const response = await TypeTicketsService.list(
        'page=0&size=1000&includeInactives=false'
      );
      if (response && response.content && Array.isArray(response.content)) {
        const type = response.content.find(t => t.id == typeId);
        return type ? type.name : typeId;
      }
      return typeId;
    } catch (error) {
      console.error('Erro ao buscar tipo de ticket:', error);
      return typeId;
    }
  };

  // Função auxiliar para resolver o nome do catálogo de serviço pelo ID
  const resolveServiceCatalogName = async catalogId => {
    try {
      if (!catalogId) return catalogId;

      // Buscar os dados do catálogo de serviço em árvore
      const response = await ServiceCatalogService.listInTreeView();
      if (response && Array.isArray(response)) {
        // Função recursiva para encontrar o caminho completo seguindo a hierarquia de IDs
        const findHierarchicalPath = (
          nodes,
          ids,
          level = 0,
          currentPath = []
        ) => {
          if (level >= ids.length || !nodes || nodes.length === 0) {
            return level === ids.length ? currentPath.join(' / ') : null;
          }

          const targetId = ids[level];
          const node = nodes.find(n => n.value == targetId);

          if (!node) {
            return null;
          }

          const newPath = [...currentPath, node.title];

          // Se é o último nível, retorna o caminho completo
          if (level === ids.length - 1) {
            return newPath.join(' / ');
          }

          // Continua procurando no próximo nível
          return findHierarchicalPath(
            node.children || [],
            ids,
            level + 1,
            newPath
          );
        };

        // Se é uma string com múltiplos IDs (separados por vírgula)
        if (typeof catalogId === 'string' && catalogId.includes(',')) {
          const ids = catalogId.split(',').map(id => parseInt(id.trim()));

          // Tenta interpretar como um caminho hierárquico único (como "1,1,1")
          const hierarchicalPath = findHierarchicalPath(response, ids);

          if (hierarchicalPath) {
            // É um caminho hierárquico único
            return hierarchicalPath;
          } else {
            // São múltiplos catálogos, busca cada um individualmente
            const paths = ids
              .map(id => {
                const path = findHierarchicalPath(response, [id]);
                return path;
              })
              .filter(path => path);
            return paths.join(', ');
          }
        }

        // Para um ID único
        const catalogPath = findHierarchicalPath(response, [catalogId]);
        return catalogPath || catalogId;
      }
      return catalogId;
    } catch (error) {
      console.error('Erro ao buscar catálogo:', error);
      return catalogId;
    }
  };

  const loadReport = async () => {
    setIsLoading(true);
    try {
      const response = await CustomReportService.findByUuid(uuid);
      if (response && !response.ok) {
        const report = response;

        // Resolver nomes dos filtros que são campos de pesquisa
        const resolvedFilters = await Promise.all(
          report.filters.map(async filter => {
            let resolvedValue = filter.value;

            // Resolver nomes baseado no tipo de campo
            if (filter.fieldId === 'TICKET_CUSTOMER') {
              resolvedValue = await resolveCustomerName(filter.value);
            } else if (filter.fieldId === 'TICKET_REQUESTER') {
              resolvedValue = await resolveContactName(filter.value);
            } else if (filter.fieldId === 'TICKET_ASSIGNEE') {
              resolvedValue = await resolveAgentName(filter.value);
            } else if (filter.fieldId === 'TICKET_SERVICE_GROUP') {
              resolvedValue = await resolveServiceGroupName(filter.value);
            } else if (filter.fieldId === 'TICKET_TYPE') {
              resolvedValue = await resolveTypeTicketName(filter.value);
            } else if (filter.fieldId === 'TICKET_SERVICE_CATALOG') {
              resolvedValue = await resolveServiceCatalogName(filter.value);
            }

            return {
              fieldId: filter.fieldId,
              operator: filter.operator,
              value: filter.value, // Manter o ID original para o backend
              displayValue: resolvedValue, // Nome para exibição
              displayName: filter.fieldDisplayName || filter.displayName,
              conditionType: filter.conditionType || 'AND'
            };
          })
        );

        // Transform the data from API format to form format
        setReportData({
          name: report.name,
          description: report.description,
          inactive: report.inactive,
          columns: report.columns.map(col => ({
            fieldId: col.fieldId,
            displayOrder: col.displayOrder,
            displayName: col.fieldDisplayName || col.displayName
          })),
          filters: resolvedFilters,
          includeTicketNotes: report.includeTicketNotes,
          includeTicketAppointments: report.includeTicketAppointments,
          schedule: {
            enabled: !!report.schedule && !report.schedule.inactive,
            frequency: report.schedule?.frequency || 'DAILY',
            scheduleDetails: report.schedule?.scheduleDetails || '',
            executionTime: report.schedule?.executionTime || '08:00',
            recipients: report.schedule?.recipients || '',
            emailSubjectTemplate: report.schedule?.emailSubjectTemplate || '',
            emailBodyTemplate: report.schedule?.emailBodyTemplate || '',
            sendAsExcel: report.schedule?.sendAsExcel || false,
            sendAsPdf: report.schedule?.sendAsPdf || false,
            sendAsCsv: report.schedule?.sendAsCsv || false,
            inactive: report.schedule?.inactive || false,
            dayOfMonth: report.schedule?.dayOfMonth || 1,
            daysOfWeek: report.schedule?.daysOfWeek || '',
            executionInterval: report.schedule?.executionInterval || 1
          }
        });
      }
    } catch (error) {
      toast.error('Erro ao carregar relatório');
      navigate('/reports/custom');
    } finally {
      setIsLoading(false);
    }
  };

  const handleNavs = targetStep => {
    if (targetStep < currentStep || validateCurrentStep()) {
      setCurrentStep(targetStep);
    }
  };

  const validateCurrentStep = () => {
    switch (currentStep) {
      case 1:
        // Validação da Etapa 1: Definições Básicas
        if (!reportData.name.trim()) {
          toast.error('Nome do relatório é obrigatório');
          return false;
        }
        return true;

      case 2:
        // Validação da Etapa 2: Dados e Filtros
        if (reportData.columns.length === 0) {
          toast.error('Selecione pelo menos uma coluna para continuar');
          return false;
        }

        if (reportData.filters.length === 0) {
          toast.error('Configure pelo menos um filtro para continuar');
          return false;
        }

        // Validar filtros se existirem
        for (let i = 0; i < reportData.filters.length; i++) {
          const filter = reportData.filters[i];
          if (!filter.fieldId) {
            toast.error(`Filtro ${i + 1}: Selecione um campo`);
            return false;
          }
          if (!filter.operator) {
            toast.error(`Filtro ${i + 1}: Selecione um operador`);
            return false;
          }
          if (
            !['IS_NULL', 'IS_NOT_NULL'].includes(filter.operator) &&
            !filter.value
          ) {
            toast.error(`Filtro ${i + 1}: Preencha o valor`);
            return false;
          }
        }
        return true;

      case 3:
        // Validação da Etapa 3: Agendamento
        if (reportData.schedule.enabled) {
          // Validar campos obrigatórios quando agendamento está ativo
          if (!reportData.schedule.frequency) {
            toast.error('Selecione a frequência do agendamento');
            return false;
          }

          if (!reportData.schedule.executionTime) {
            toast.error('Defina o horário de execução');
            return false;
          }

          if (
            !reportData.schedule.executionInterval ||
            reportData.schedule.executionInterval < 1
          ) {
            toast.error('Defina o intervalo de execução (mínimo 1)');
            return false;
          }

          // Validações específicas por frequência
          if (
            reportData.schedule.frequency === 'WEEKLY' &&
            !reportData.schedule.daysOfWeek
          ) {
            toast.error('Selecione pelo menos um dia da semana');
            return false;
          }

          if (
            reportData.schedule.frequency === 'MONTHLY' &&
            !reportData.schedule.dayOfMonth
          ) {
            toast.error('Selecione o dia do mês');
            return false;
          }

          // Validações de e-mail
          if (
            !reportData.schedule.recipients ||
            reportData.schedule.recipients.trim() === ''
          ) {
            toast.error('Defina pelo menos um destinatário');
            return false;
          }

          if (
            !reportData.schedule.emailSubjectTemplate ||
            reportData.schedule.emailSubjectTemplate.trim() === ''
          ) {
            toast.error('Preencha o assunto do e-mail');
            return false;
          }

          if (
            !reportData.schedule.emailBodyTemplate ||
            reportData.schedule.emailBodyTemplate.trim() === ''
          ) {
            toast.error('Preencha o corpo do e-mail');
            return false;
          }

          // Validar pelo menos um formato de anexo
          if (
            !reportData.schedule.sendAsExcel &&
            !reportData.schedule.sendAsPdf &&
            !reportData.schedule.sendAsCsv
          ) {
            toast.error('Selecione pelo menos um formato de anexo');
            return false;
          }
        }
        return true;

      default:
        return true;
    }
  };

  const handleNext = () => {
    if (validateCurrentStep()) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    setCurrentStep(currentStep - 1);
  };

  const transformDataForAPI = () => {
    // Função para processar valores de filtros relacionais
    const processFilterValue = filter => {
      const field = CUSTOM_REPORT_FIELDS.find(
        f => f.fieldId === filter.fieldId
      );

      // Se for um campo relacional, garantir que o valor seja tratado como ID
      if (field && field.hasJoin) {
        // Manter o valor como está, pois agora já é o ID selecionado do select
        return filter.value;
      }

      // Para outros tipos, manter o valor original
      return filter.value;
    };

    return {
      name: reportData.name,
      description: reportData.description,
      inactive: reportData.inactive,
      columns: reportData.columns.map((col, index) => ({
        fieldId: col.fieldId, // Enviar fieldId para o backend processar corretamente
        displayOrder: index
      })),
      filters: reportData.filters.map(filter => ({
        fieldId: filter.fieldId, // Enviar fieldId para o backend processar corretamente
        operator: filter.operator,
        value: processFilterValue(filter),
        conditionType: filter.conditionType || 'AND'
      })),
      includeTicketNotes: reportData.includeTicketNotes,
      includeTicketAppointments: reportData.includeTicketAppointments,
      schedule: reportData.schedule.enabled
        ? {
            frequency: reportData.schedule.frequency,
            daysOfWeek: reportData.schedule.daysOfWeek,
            dayOfMonth: reportData.schedule.dayOfMonth,
            executionTime: reportData.schedule.executionTime,
            recipients: reportData.schedule.recipients,
            executionInterval: reportData.schedule.executionInterval,
            emailSubjectTemplate: reportData.schedule.emailSubjectTemplate,
            emailBodyTemplate: reportData.schedule.emailBodyTemplate,
            inactive: !reportData.schedule.enabled,
            sendAsExcel: reportData.schedule.sendAsExcel,
            sendAsPdf: reportData.schedule.sendAsPdf,
            sendAsCsv: reportData.schedule.sendAsCsv
          }
        : null
    };
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const data = transformDataForAPI();
      let response;

      if (isEditMode) {
        response = await CustomReportService.update(uuid, data);
      } else {
        response = await CustomReportService.create(data);
      }

      if (response && !response.ok) {
        toast.success(
          isEditMode
            ? 'Relatório atualizado com sucesso'
            : 'Relatório criado com sucesso'
        );
        navigate('/reports/custom');
      } else {
        toast.error('Erro ao salvar relatório');
      }
    } catch (error) {
      toast.error('Erro ao salvar relatório');
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <Card>
        <Card.Body className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <CustomReportLayout>
      <div className="custom-report-wizard">
        {/* Progress Steps */}
        <Card className="mb-1 p-2 border-0 shadow-sm">
          <Card.Body className="p-0">
            <div className="wizard-steps">
              {navItems.map((item, index) => (
                <WizardStepItem
                  key={item.label}
                  index={index + 1}
                  step={currentStep}
                  number={index + 1}
                  label={item.label}
                  icon={item.icon}
                  isLast={index === navItems.length - 1}
                  onClick={() => handleNavs(index + 1)}
                />
              ))}
            </div>
          </Card.Body>
        </Card>

        {/* Content Card */}
        <Card className="border-0 shadow-sm">
          <Card.Body>
            <div className="wizard-content">
              {currentStep === 1 && <BasicDefinitionsStep />}
              {currentStep === 2 && <DataAndFiltersStep />}
              {currentStep === 3 && <SchedulingStep />}
              {currentStep === 4 && <SummaryStep />}
            </div>

            {/* Navigation Buttons */}
            <div className="wizard-navigation">
              <Flex justifyContent="between" alignItems="center">
                <Button
                  variant="falcon-default"
                  onClick={
                    currentStep === 1
                      ? () => navigate('/reports/custom')
                      : handlePrevious
                  }
                >
                  <FontAwesomeIcon
                    icon={isRTL ? 'chevron-right' : 'chevron-left'}
                    className="me-1"
                  />
                  {currentStep === 1 ? 'Cancelar' : 'Anterior'}
                </Button>

                {currentStep < 4 ? (
                  <Button variant="primary" onClick={handleNext}>
                    Próximo
                    <FontAwesomeIcon
                      icon={isRTL ? 'chevron-left' : 'chevron-right'}
                      className="ms-1"
                    />
                  </Button>
                ) : (
                  <Button onClick={handleSave} disabled={isSaving}>
                    <FontAwesomeIcon icon={faSave} className="me-1" />
                    {isSaving ? 'Salvando...' : 'Salvar'}
                  </Button>
                )}
              </Flex>
            </div>
          </Card.Body>
        </Card>
      </div>
    </CustomReportLayout>
  );
};

// Componente para cada item do wizard baseado no estilo da imagem
const WizardStepItem = ({
  index,
  step,
  number,
  label,
  icon,
  isLast,
  onClick
}) => {
  const isActive = step === index;
  const isCompleted = step > index;

  return (
    <div className="wizard-step-item">
      <span
        className={classNames(
          'wizard-step-circle',
          isActive ? 'active' : isCompleted ? 'completed' : 'pending'
        )}
        onClick={onClick}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onClick();
          }
        }}
      >
        {isCompleted ? (
          <FontAwesomeIcon icon="check" size="sm" />
        ) : (
          <FontAwesomeIcon icon={icon} size="sm" />
        )}
      </span>

      {/* Linha conectora - sempre mostra, mas última linha fica cinza */}
      <div
        className={classNames(
          'wizard-step-connector',
          isCompleted ? 'completed' : '',
          isLast ? 'last-connector' : ''
        )}
      />

      <span
        className={classNames(
          'wizard-step-label',
          isActive ? 'active' : isCompleted ? 'completed' : 'pending'
        )}
      >
        {label}
      </span>
    </div>
  );
};

WizardStepItem.propTypes = {
  index: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  number: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  isLast: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
};

const CustomReportWizard = () => {
  return (
    <CustomReportProvider>
      <CustomReportWizardContent />
    </CustomReportProvider>
  );
};

export default CustomReportWizard;
